import React, { useContext, useState } from 'react';
import { CButton, CCardBody, CCol, CImg, CRow, CTooltip } from '@coreui/react';
import moment from 'moment';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useSelector } from 'react-redux';
import { Job } from '../../shared/models/jobs';
import { ConfigContext } from '../../shared/contexts/journeyConfig.context';
import diffAsString, { formatDuration } from '../../shared/utils/time-utils';
import { formatJobDescription, getEmails, getPhones } from '../../shared/utils/job-utils';
import ViewJobImagesModal from '../../jobs/containers/modals/ViewJobImagesModal';
import FeedbackRating from '../../shared/components/FeedbackRating';
import tooltip from '../../assets/images/tool_tip.svg';
import { selectOrgHierarchySelectedOrg } from '../../store/selectors/orgHierarchy.selectors';

const MAX_WAITTIME = 15;

const METER_TO_MILES_RATIO = 1609;

interface ColumnSizes {
  [key: string]: number;
};

interface JobDetailsContainerProps {
  job: Job;
  columnSizes?: ColumnSizes;
}

const JobsDetailsContainer = (props: JobDetailsContainerProps) => {
  const { job, columnSizes = {} } = props;
  const [showViewImage, setShowViewImage] = useState(false);
  const labels = useContext(ConfigContext);
  const selectedOrg = useSelector(selectOrgHierarchySelectedOrg);

  const id = job?._id;
  const isSessionsEnabled = selectedOrg?.config?.sessions?.enabled;
  const description = formatJobDescription(job.description, job?.foreign_id, isSessionsEnabled);
  const destination = job?.destination?.name;
  const apptFrom = moment(+job?.metadata?.find(meta => meta.n === 'appt_from')?.v || 0);
  const apptTo = moment(+job?.metadata?.find(meta => meta.n === 'appt_to')?.v || 0);
  const customer = (job?.metadata?.find(meta => meta.n === 'era_recipient_name')?.v || '') as string;
  const appointmentID = (job?.metadata?.find(meta => meta.n === 'appointment_id')?.v || job?.foreign_id) as string | number;
  const phones = getPhones(job?.recipients)?.filter(item => item).map(item => {
    try {
      return parsePhoneNumber(item).formatNational()
    } catch (e) {
      // It's possible that tasks created before have an incorrect format and will cause the
      // page to crash, so we'll just return the unformatted version for now
      return item;
    }
  });
  const emails = getEmails(job?.recipients)?.filter(item => item);
  const timeOnSite = job?.completed_time && job?.arrived_time && Math.floor((job.completed_time - job.arrived_time)/1000/60);
  const hasArrivedTime = job?.arrived_time !== undefined;
  const arrivedTime = moment(job?.arrived_time);
  const dueTime = job?.due_time > 0 ? moment(job.due_time) : null;
  const distanceTraveled = Math.round((job?.aggregations?.distance_traveled || 0) * 100 / METER_TO_MILES_RATIO) / 100;
  const completedReasonCode = job?.metadata?.find(obj => obj.n === 'completed_reasoncode')?.v as string;

  return job && (
    <CCardBody className="description-slot" key={id} onClick={(e) => e.stopPropagation()}>
      {showViewImage && <ViewJobImagesModal job={ job } show={ showViewImage } close={ () => {
        setShowViewImage(false)
      } }/>}
      <CRow className="mb-1">
        <CCol md={columnSizes.appointmentID || 3}>
          <div>
            <strong>{ labels.task } #:</strong>
            <CTooltip placement="top" content={`Customer’s identifier for ${labels.task.toLowerCase()}`}>
              <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
            </CTooltip>
          </div>
          <div>{ appointmentID || '-' }</div>
        </CCol>
        <CCol md={columnSizes.id || 5}>
          <div>
            <strong>Glympse #:</strong>
            <CTooltip placement="top" content="Internal Glympse ID used for support requests">
              <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
            </CTooltip>
          </div>
          <div>{ id }</div>
        </CCol>
        <CCol md={columnSizes.createdTime || 2}>
          <div>
            <strong>Created:</strong>
            <CTooltip placement="top" content={`Time of ${labels.task.toLowerCase()} creation`}>
              <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
            </CTooltip>
          </div>
          <div>
            { moment(job?.created_time).format('lll') }
          </div>
        </CCol>
        <CCol md={columnSizes.startedTime || 2}>
          <div>
            <strong>Started:</strong>
          </div>
          <div>
            { moment(job?.start_time).format('lll') }
          </div>
        </CCol>
      </CRow>
      <CRow className="mb-1">
        <CCol md={columnSizes.customerName || 3}>
          <div>
            <strong>Customer:</strong>
          </div>
          <div>{ customer || 'N/A' }</div>
        </CCol>
        <CCol md={columnSizes.apptTime || 5}>
          <div>
            <strong>Appointment Window Time:</strong>
          </div>
          <div>
            { !apptTo.isSame(apptFrom) ? `${apptFrom.format('lll')} - ${apptTo.format('lll')}` : '-'}
          </div>
        </CCol>
        <CCol md={columnSizes.promisedTime || 2}>
          <div>
            <strong>Promised Time:</strong>
            <CTooltip placement="top" content={`Expected time of  ${labels.task.toLowerCase()}`}>
              <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
            </CTooltip>
          </div>
          <div>
            { dueTime ? dueTime.format('lll') : '-' }
          </div>
        </CCol>
        <CCol md={columnSizes.completedTime || 2}>
          <div>
            <strong>Completed Time:</strong>
            <CTooltip placement="top" content={`${labels.agent} updated status of the ${labels.task.toLowerCase()} to ‘Completed’`}>
              <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
            </CTooltip>
          </div>
          <div>
            { job?.completed_time !== undefined ? moment(job?.completed_time).format('lll') : '-' }
          </div>
        </CCol>
      </CRow>
      <CRow className="mb-1">
        <CCol md={columnSizes.phones || 3}>
          <div>
            <strong>Phone Number(s):</strong>
          </div>
          {
            phones?.length > 0 ? phones.map(phone => <div>{ phone }</div>) : '-'
          }
        </CCol>
        <CCol md={columnSizes.destination || 5}>
          <div>
            <strong>Destination:</strong>
            <CTooltip placement="top" content={`${labels.task} address`}>
              <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
            </CTooltip>
          </div>
          <div>{ destination || 'N/A' }</div>
        </CCol>
        <CCol md={columnSizes.arrivalTime || 2}>
          <div>
            <strong>Arrival Time:</strong>
            <CTooltip placement="top" content={`Time when the ${labels.agent.toLowerCase()} arrived onsite.`}>
              <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
            </CTooltip>
          </div>
          <div>
            { hasArrivedTime ? moment(job?.arrived_time).format('lll') : 'N/A' }
          </div>
        </CCol>
        <CCol md={columnSizes.completedReasonCode || 2}>
          {!!completedReasonCode && <div>{completedReasonCode}</div>}
        </CCol>
        <CCol md={columnSizes.timeOnSite || 2}>
          <div>
            <strong>Time On Site:</strong>
            <CTooltip placement="top" content="The amount of time between the arrived time and the completed time">
              <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
            </CTooltip>
          </div>
          <div>
            { (timeOnSite !== undefined && timeOnSite > 0) ? `${ formatDuration(moment.duration(timeOnSite,  'minutes'))}` : '-'}
          </div>
        </CCol>
      </CRow>
      <CRow className="mb-1">
        <CCol md={columnSizes.emails || 8}>
          <div>
            <strong>Email(s):</strong>
          </div>
          {
            emails?.length > 0 ? emails.map(email => <div>{ email }</div>) : '-'
          }
        </CCol>
        <CCol md={columnSizes.waitTime || 2}>
          <div>
            <strong>Late:</strong>
            <CTooltip placement="top" content="If “No,” arrived time is >= appointment start time. Otherwise it displays the amount of time between the appointment start time and the arrival time">
              <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
            </CTooltip>
          </div>
          { hasArrivedTime && dueTime ? (
            <div className={`${arrivedTime.diff(dueTime, 'minutes') > MAX_WAITTIME && 'text-danger'}`}>
              { arrivedTime.isSameOrBefore(dueTime) ? 'No' : `Yes, ${diffAsString(dueTime, arrivedTime)}` }
            </div>
          ) : (
            <div>N/A</div>
          ) }
        </CCol>
        { job?.aggregations?.distance_traveled !== undefined && (
          <CCol md={columnSizes.distanceTraveled || 2}>
            <div>
              <strong>Distance Traveled:</strong>
              <CTooltip placement="top" content="Length of distance measured in miles">
                <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
              </CTooltip>
            </div>
            <div>{ distanceTraveled } miles</div>
          </CCol>
        ) }
      </CRow>
      <CRow className="mb-1">
        <CCol md={columnSizes.description || (job?.feedback ? 8 : 12)}>
          <div>
            <strong>Description:</strong>
            <CTooltip placement="top" content={`Non-customer-facing field to give detail about ${labels.task.toLowerCase()}`}>
              <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
            </CTooltip>
          </div>
          <div className="job-description">{ description || '-'}</div>
        </CCol>
        { job?.feedback && (
          <>
            <CCol md={columnSizes.feedbackRating || 4}>
              <div className="d-flex align-items-center">
                <strong>Feedback Rating:</strong>
                <FeedbackRating rating={job?.feedback?.rating} containerClassName="ml-1"/>
              </div>
              <div>{ job.feedback?.comment }</div>
            </CCol>
          </>
        )}
      </CRow>
      { job.images?.length > 0 && (
        <CRow className="mb-1">
          <CCol md={columnSizes.images || 12}>
            <div>
              <strong>Images:</strong>
            </div>
            <div>
              <CButton
                onClick={(e) => {
                  e.stopPropagation();
                  setShowViewImage(true)
                }}
                size="sm"
                className="bg-color-orange text-white roundedBtn"
              >
                Click to view
              </CButton>
            </div>
          </CCol>
        </CRow>
      )}
    </CCardBody>
  );
};

JobsDetailsContainer.defaultProps = {
  columnSizes: {}
};

export default JobsDetailsContainer;
